import React, { memo, useCallback, useId, useState } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, number, oneOfType, string,
} from 'prop-types'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

import Thumbnail from './Components/Thumbnail/Thumbnail'
import Toggle from './Components/Toggle/Toggle'
import useRunReferences from './useRunReferences'

// A column down the canvas card's top-left on desktop (Figma 11216:303046), a strip along
// its bottom on phones (11225:307203), or a labelled row in the host's flow that scrolls
// sideways (8972:465570). Past this many, each layout shows only that many behind the
// toggle; the row shows them all.
const COLLAPSED_COUNT = { column: 3, strip: 1, row: Infinity }

const PHONE_BREAKPOINTS = ['xs']

const getLayout = (breakpoint, stripBreakpoints, rowBreakpoints) => {
  if (rowBreakpoints.includes(breakpoint)) return 'row'
  if (stripBreakpoints.includes(breakpoint)) return 'strip'
  return 'column'
}

// The images an image was generated from, over the full-width editor's canvas and the
// opened image modal's stage. Stays mounted while the editor's canvas switches versions
// (isShown toggles), so the run is fetched once. customClass lets a host adjust its placement,
// and stripBreakpoints / rowBreakpoints name the breakpoints those layouts apply at.
function References({
  runId = null,
  isShown = true,
  customClass = '',
  stripBreakpoints = PHONE_BREAKPOINTS,
  rowBreakpoints = EMPTY_ARRAY,
}) {
  const t = useTranslation()
  const labelId = useId()
  const references = useRunReferences(runId)
  const layout = getLayout(useBreakpoint(), stripBreakpoints, rowBreakpoints)
  const isStrip = layout === 'strip'
  const [isExpanded, setIsExpanded] = useState(false)

  const handleToggle = useCallback(() => setIsExpanded(previous => !previous), [])

  if (!isShown || references.length === 0) {
    return null
  }

  const isCollapsible = references.length > COLLAPSED_COUNT[layout]
  const shown = isCollapsible && !isExpanded ? references.slice(0, COLLAPSED_COUNT[layout]) : references
  // The strip and the row draw a divider between the uploaded images and the styles.
  const firstStyleIndex = layout === 'column' ? -1 : references.findIndex(reference => reference.isStyle)
  const label = t('shared.references.label')

  return (
    <div
      className={classnames('image-references', `image-references--${layout}`, customClass)}
      role="group"
      aria-labelledby={isStrip ? undefined : labelId}
      aria-label={isStrip ? label : undefined}
    >
      {!isStrip && (
        <p id={labelId} className="image-references__label cgt-caption">{label}</p>
      )}
      <ul
        className={classnames(
          `image-references__list image-references__list--${layout}`,
          isStrip && isExpanded && 'image-references__list--under-toggle',
        )}
        // Expanded, and always in the row, the list can overflow and scroll with its
        // scrollbar hidden, and the thumbnails take no focus, so the list itself is the
        // keyboard's way to scroll it.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={isExpanded || layout === 'row' ? 0 : undefined}
      >
        {shown.map((reference, index) => (
          <Thumbnail
            key={reference.id}
            url={reference.url}
            layout={layout}
            isDivided={firstStyleIndex > 0 && index === firstStyleIndex}
          />
        ))}
      </ul>
      {isCollapsible && <Toggle layout={layout} isExpanded={isExpanded} onToggle={handleToggle} />}
    </div>
  )
}

References.propTypes = {
  runId: oneOfType([string, number]),
  isShown: bool,
  customClass: string,
  stripBreakpoints: arrayOf(string),
  rowBreakpoints: arrayOf(string),
}

export default memo(References)
